<template>
  <div class="map-wrapper">
    <google-map
      v-if="map.isLoaded"
      :center="mapCenter"
      :zoom="map.zoom"
      map-type-id="roadmap"
      class="map"
      :options="map.options"
      @bounds_changed="loadPoints"
      @click="addPoint"
    >
      <google-map-cluster>
        <google-map-marker
          v-for="(point, index) in map.points"
          :key="index"
          :position="point.coordinate"
          :label="{
            text: point.name,
            fontSize: '11px',
            color: '#2c3e50',
            fontWeight: '500',
          }"
          :icon="{
            labelOrigin: {
              x: 10,
              y: 30,
            },
            url: require('@/assets/dot.svg'),
          }"
          :clickable="true"
          @click="openPlaceInfo(point)"
        />
      </google-map-cluster>

      <google-map-marker
        v-if="map.isDetected"
        :position="map.userLocation"
        :animation="2"
        :icon="{
          url: require('@/assets/point.svg'),
          scale: 1,
          scaledSize: { height: 20, width: 20 },
          anchor: { x: 10, y: 10 },
        }"
      />

      <google-map-marker
        v-if="isAddModeEnable && add.center"
        :draggable="true"
        :position="add.center"
        :icon="{
          url: require('@/assets/green-dot.svg'),
          scale: 1,
          scaledSize: { height: 30, width: 30 },
        }"
        @dragend="changeNewPointPosition"
      />
    </google-map>

    <loader v-if="map.isLoading" />

    <v-alert
      v-if="!isAddModeEnable && !map.isLoading && !map.isDetected"
      class="mx-5 my-5"
      dense
      dismissible
      transition="scale-transition"
      border="left"
      elevation="5"
      colored-border
      type="warning"
    >
      Мы не смогли определить Ваше местоположение. Но хотя бы попытались.
    </v-alert>

    <v-alert
      v-if="isAddModeEnable && hasAddMarker"
      class="mx-5 my-5"
      dense
      transition="scale-transition"
      elevation="5"
      colored-border
      type="info"
      :icon="false"
    >
      <p class="py-0 mb-0">
        В случае необходимости Вы можете переместить точку. После того, как точка будет установлена
        - нажмите кнопку "Продолжить".
      </p>

      <v-row justify="space-between">
        <v-col cols="auto" class="py-0">
          <v-btn color="blue darken-1" small text @click="disableAddMode">Закрыть</v-btn>
        </v-col>

        <v-col cols="auto" class="py-0">
          <v-btn color="primary" small @click="confirmNewLocation">Продолжить</v-btn>
        </v-col>
      </v-row>
    </v-alert>
  </div>
</template>

<script>
import * as VueGoogleMaps from 'vue2-google-maps';
import GoogleMapCluster from 'vue2-google-maps/src/components/cluster';
import lodash from 'lodash';
import Vue from 'vue';
import { mapGetters } from 'vuex';
import PlaceService from '@/services/PlaceService';
import Loader from '@/components/Loader';
import mapOptions from '@/mapOptions';

Vue.use(VueGoogleMaps, {
  load: {
    key: process.env.VUE_APP_GOOGLE_MAPS_KEY,
    libraries: 'places',
  },
});

export default {
  name: 'GoogleMap',

  components: {
    Loader,
    googleMap: VueGoogleMaps.Map,
    googleMapMarker: VueGoogleMaps.Marker,
    GoogleMapCluster,
  },

  props: {
    isOpen: {
      type: Boolean,
      required: false,
      default: false,
    },

    delivery: {
      type: Boolean,
      required: false,
      default: false,
    },

    pickup: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    let cityCenters = [
      { lat: 55.765505, lng: 37.624754 }, // Moscow
    ];

    if (process.env.NODE_ENV === 'production') {
      cityCenters = [
        { lat: 55.765505, lng: 37.624754 }, // Moscow
        { lat: 59.93863, lng: 30.31413 }, // SPb
        { lat: 48.71939, lng: 44.50183 }, // Volgograd
        { lat: 47.23135, lng: 39.72328 }, // Rostov on Don
      ];
    }

    return {
      cityCenters,

      map: {
        center: undefined,
        points: [],
        options: mapOptions,
        isLoading: true,
        isLoaded: false,
        isDetected: false,
        zoom: 12,
        userLocation: undefined,
      },

      add: {
        center: undefined,
      },
    };
  },

  computed: {
    ...mapGetters(['mapCenter', 'isAddModeEnable']),

    hasAddMarker() {
      return this.add.center;
    },
  },

  watch: {
    isOpen() {
      this.fetchData();
    },

    delivery() {
      this.fetchData();
    },

    pickup() {
      this.fetchData();
    },
  },

  mounted() {
    this.detectLocation();
  },

  methods: {
    disableAddMode() {
      this.add.center = undefined;
      this.$store.dispatch('map/disableAddMode');
    },

    addPoint(e) {
      if (!this.isAddModeEnable) {
        return;
      }

      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      this.add.center = position;
      this.setMapCenterPosition(position);
    },

    confirmNewLocation() {
      this.$emit('click', this.add.center);
      this.add.center = undefined;
    },

    changeNewPointPosition(e) {
      const position = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };
      this.add.center = position;
      this.setMapCenterPosition(position);
    },

    setMapCenterPosition(position) {
      this.$store.dispatch('map/setCenter', position);
    },

    detectLocation() {
      if (!navigator.geolocation) {
        this.setRandomLocation();
        this.map.isLoaded = true;
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (result) => {
          const position = { lat: result.coords.latitude, lng: result.coords.longitude };

          this.setMapCenterPosition(position);
          this.map.userLocation = position;
          this.map.isLoaded = true;
          this.map.isDetected = true;
          this.map.zoom = 14;
        },
        (error) => {
          this.setRandomLocation();
          this.map.isLoaded = true;
          console.log(error);
        },
        {
          timeout: 3000,
          enableHighAccuracy: true,
          maximumAge: 0,
        }
      );
    },

    setRandomLocation() {
      const position = this.cityCenters[Math.floor(Math.random() * this.cityCenters.length)];
      this.setMapCenterPosition(position);
    },

    openPlaceInfo(point) {
      if (point.slug) {
        this.$router.push({
          name: 'place-view-slug',
          params: {
            slug: point.slug,
            id: point.id,
          },
        });
      } else {
        this.$router.push({
          name: 'place-view',
          params: {
            id: point.id,
          },
        });
      }
    },

    // Функцию не сокращать. В противном случае не работает this.
    // eslint-disable-next-line
    loadPoints: lodash.debounce(function (bounds) {
      this.$store.dispatch('map/setBounds', bounds.toJSON());
      this.fetchData();
    }, 500),

    fetchData() {
      PlaceService.findByBounds()
        .then((response) => {
          this.map.points = response;
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.map.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.map-wrapper {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}

.map {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
}
</style>
